import {Component, Input, OnInit, OnDestroy, AfterViewInit, OnChanges, SimpleChanges} from '@angular/core';
import {UnitsManager} from "../services/units-manager";
import {PageTitleService} from "../../shared/services/page-title.service";
import {Observable, Subject, takeUntil} from "rxjs";
import {IotService} from "../../shared/services/iot.service";
import {LocalizerService, ScreenService} from "../../shared";
import {FarfisaDevice} from "../../shared/models/devices/farfisa_device";
import {UnitDashboard} from "../models/unit-dashboard";
import {UnitCallEventsFilters} from "../models/unit-call-events-filters";
import {CallEvent} from "../models/call-event";
import {BaseDashboardComponent} from "../../shared/components/base-dashboard.component";
import {Session} from "../../shared/models/webrtc/call/call";
import {CallSessionManager} from "../../shared/services/call-session-manager.service";
import {CallEntityKind, CallParticipant} from "../../shared/models/webrtc/call/v2/call-entity";
import { Store } from '@ngrx/store';
import { selectCurrentCall, selectCallForEntity } from '../../state/call/current-call.selector';
import { CurrentCallActions } from '../../state/call/current-call.actions';
import { CallEntry } from '../../state/call/current-call-state';

@Component({
    selector: 'app-unit-dashboard',
    templateUrl: './unit-dashboard.component.html',
    styleUrl: './unit-dashboard.component.scss'
})

export class UnitDashboardComponent extends BaseDashboardComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input() unitId: string | null = null;

    unit: UnitDashboard | null = null;
    deviceNotificationToggleStates: Map<string, boolean> = new Map();
    private entitySub$ = new Subject<void>();

    constructor(screen: ScreenService, localizer: LocalizerService, private unitsManager: UnitsManager,
        callSessionManager: CallSessionManager, iot: IotService, store: Store,
        private pageTitle: PageTitleService) {
        super(screen, localizer, callSessionManager, iot, store);
    }

    protected buildCaller(): CallParticipant {
        return {
            id: this.unitId!,
            kind: CallEntityKind.Unit
        };
    }

    //#region Lifecycle

    ngOnInit() {

        this.setupEntitySubscriptions();

        // Subscribe to Redux state to sync local variables
        this.store.select(selectCurrentCall).pipe(
            takeUntil(this.onDestroy)
        ).subscribe(currentCallState => {
            if (!currentCallState) {
                this.currentCallingEntity = null;
                this.currentCallingEntityName = null;
                this.currentCallingEntityOutputs = undefined;
                this.currentCallingEntityThingName = null;
                return;
            }

            // Derive the active leg: pipLeg if speaking/outgoing, otherwise mainLeg
            let leg = currentCallState.mainLeg ?? null;
            if (currentCallState.pipLeg && (currentCallState.pipLeg.status === 'speaking' || currentCallState.pipLeg.status === 'outgoing')) {
                leg = currentCallState.pipLeg;
            }

            this.currentCallingEntity = leg?.entity ?? null;
            this.currentCallingEntityName = leg?.entityName || this.resolveCallerName(leg?.entity) || null;
            this.currentCallingEntityOutputs = leg?.entityOutputs;
            this.currentCallingEntityThingName = leg?.entityThingName ?? null;

            // Enrich Redux with local dashboard data if entity info is missing
            this.enrichLegEntityInfo(currentCallState.callId!, leg);
        });

        this.loadDashboard();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['unitId'] && !changes['unitId'].firstChange) {
            this.setupEntitySubscriptions();
            this.loadDashboard();
        }
    }

    ngAfterViewInit() {
        this.checkForAnsweringCallSession(this.unitId!);
    }

    private setupEntitySubscriptions(): void {
        // Unsubscribe from previous entity-specific subscriptions
        this.entitySub$.next();

        if (!this.unitId) return;

        // Set the call for this entity as active (so device-live-view picks it up)
        this.store.select(selectCallForEntity(this.unitId)).pipe(
            takeUntil(this.entitySub$),
            takeUntil(this.onDestroy)
        ).subscribe(call => {
            if (call?.callId) {
                this.store.dispatch(CurrentCallActions.setActiveCall({ callId: call.callId }));
            }
        });

        // Re-check for answering call session
        this.checkForAnsweringCallSession(this.unitId);
    }

    //#endregion

    unitEventsProvider = ({installationId, entityId, pagination, filters}: {
        installationId: string;
        entityId: string;
        pagination?: any;
        filters?: UnitCallEventsFilters;
    }): Observable<CallEvent[]> => {
        return this.unitsManager.getUnitCallEvents(installationId, entityId, pagination, filters);
    };


    viewIncomingCall(session: Session, answer: boolean) {
        this.incomingCallCardVisible = false;
        this.incomingCall = null;

        if (answer) {
            this.callSessionManager.startDeviceTalkSession(session.callId, session.sessionId);
        }
    }


    //#region Device card methods

    toggleDeviceNotification(device: any) {
        if (!device) return;

        const currentState = device.notification_preferences?.enabled ?? true;
        const newState = !currentState;

        this.deviceNotificationToggleStates.set(device.id, true);
        this.unitsManager.toggleNotifications(
            this.installationId!,
            this.unitId!,
            newState,
            device.id
        ).subscribe({
            next: (updatedDevice) => {
                this.deviceNotificationToggleStates.set(device.id, false);
                if (this.unit?.devices) {
                    const deviceIndex = this.unit.devices.findIndex(d => d.id === device.id);
                    if (deviceIndex !== -1) {
                        this.unit.devices[deviceIndex] = {...this.unit.devices[deviceIndex], ...updatedDevice};
                    }
                }
            },
            error: (error) => {
                this.deviceNotificationToggleStates.set(device.id, false);
                this.displayLocalizedErrorNotification(error); // TODO: localize error messages
                console.log(error);
            }
        });
    }

    liveViewButtonClick(device: FarfisaDevice) {
        // Start an outgoing call to a device (live view)
        const callee: CallParticipant = {
            id: device.id,
            kind: CallEntityKind.Doorstation,
            name: device.name,
            outputs: device.available_outputs,
            thingName: device.thing_name
        };
        this.startOutgoingCall(callee);
    }

    //#endregion device card methods

    //#region Private methods

    private loadDashboard(): void {
        if (!this.installationId || !this.unitId) {
            return;
        }

        // Reset state for the new dashboard
        this.unit = null;
        this.devices = [];
        this.shadowUpdates = [];
        this.pageTitle.setTitle(null);

        this.isBusy = true;
        this.unitsManager.getUnitDashboard(this.installationId, this.unitId).subscribe({
            next: (d) => {
                this.isBusy = false;
                this.unitDataRetrieved(d);
            },
            error: () => this.isBusy = false
        });
    }

    private unitDataRetrieved(unit: UnitDashboard) {
        if (!unit) {
            return;
        }

        this.unit = unit;

        if (this.unit.devices) {
            this.devices = this.unit.devices
            this.subscribeToDeviceShadows();
        }

        this.pageTitle.setTitle(this.unit.name ?? this.unit.alias ?? null);
        this.enrichActiveCallFromDevices();
    }

    /**
     * Resolve caller name locally from dashboard data (devices).
     */
    private resolveCallerName(entity: { id: string; kind: CallEntityKind } | null | undefined): string | null {
        if (!entity || !this.unit?.devices) return null;

        if (entity.kind === CallEntityKind.Doorstation) {
            const device = this.unit.devices.find(d => d.id === entity.id) as FarfisaDevice;
            if (device) {
                this.currentCallingEntityOutputs = device.available_outputs;
                this.currentCallingEntityThingName = device.thing_name;
                return device.name;
            }
        }
        return null;
    }



    //#endregion private methods
    protected readonly CallEntityKind = CallEntityKind;
}
